import { Link } from '../../components/SmartLink';
import {
  CheckCircle, Warning, MapPin, WhatsappLogo,
  Clock, Globe, ArrowRight, Shield, Airplane,
  Users, SealCheck, CalendarBlank,
  FileText, Star, Buildings, CurrencyDollar, GraduationCap, Briefcase,
} from '@phosphor-icons/react';
import VisaDetailLayout from '../../components/visas/VisaDetailLayout';
import { VISA_BY_ID } from '../../data/visaData';
import { buildWAUrl } from '../../lib/whatsapp';
import { ExpandableTip, AIAnswerBox } from '../../components/visas/VisaPageHelpers';

const WA_URL = buildWAUrl(
  'Hi Big Bird Travel, I would like to check my eligibility for the UAE Golden Visa (investor / professional / student). Please advise the category and cost that fits my case.'
);

const SUMMARY_CARDS = [
  { label: 'Validity', value: '5 or 10 Years', sub: 'Renewable, self-sponsored', Icon: CalendarBlank },
  { label: 'Local Sponsor', value: 'Not Required', sub: 'Independent of employer', Icon: Shield },
  { label: 'Family Sponsorship', value: 'Spouse, Kids, Parents', sub: 'Usually no salary rule', Icon: Users },
  { label: 'Categories', value: '6 Eligibility Paths', sub: 'Investor to student', Icon: FileText },
  { label: 'Apply From', value: 'Abu Dhabi / Dubai', sub: 'Nomination or self-apply', Icon: MapPin },
];

const GUIDE_FOR = [
  { Icon: Buildings,      label: 'Real Estate & Business Investors' },
  { Icon: Briefcase,      label: 'Entrepreneurs & Company Owners' },
  { Icon: Star,           label: 'Specialised Talents & Executives' },
  { Icon: Users,          label: 'Highly-Skilled Employees' },
  { Icon: GraduationCap,  label: 'Outstanding Students & Graduates' },
  { Icon: Globe,          label: 'Abu Dhabi, Dubai & Northern Emirates Residents' },
];

const CATEGORY_TABLE = [
  { category: 'Real Estate Investor', duration: '5 or 10 years', key: 'Property value above government threshold', badge: 'blue' },
  { category: 'Business Investor / Entrepreneur', duration: '5 or 10 years', key: 'UAE company or qualifying investment', badge: 'blue' },
  { category: 'Specialised Talent', duration: '10 years', key: 'Doctors, scientists, executives, creatives', badge: 'navy' },
  { category: 'Skilled Employee', duration: '5 years', key: 'Eligible salary + qualification level', badge: 'blue' },
  { category: 'Outstanding Student / Graduate', duration: '5 or 10 years', key: 'Top grades from eligible institution', badge: 'green' },
  { category: 'Humanitarian Pioneer', duration: '5 years', key: 'Recognised humanitarian / frontline work', badge: 'gray' },
];

const HIDDEN_FACTS = [
  {
    Icon: Shield,
    title: 'It survives a job change — that’s the whole point',
    desc: 'Unlike a standard employment visa, the Golden Visa is not cancelled if you resign, get laid off, or switch employers. It is self-sponsored, so your right to stay in the UAE is not tied to any one company.',
  },
  {
    Icon: Users,
    title: 'Family sponsorship usually skips the salary rule',
    desc: 'Standard UAE family sponsorship requires the sponsor to meet a minimum salary. Golden Visa holders can typically sponsor a spouse and children without that requirement — one of the visa’s most valuable perks for families.',
  },
  {
    Icon: CurrencyDollar,
    title: 'Property investment can sometimes be a mortgage',
    desc: 'Depending on the bank and conditions, some real-estate investor applicants can qualify with a mortgaged property rather than a fully-paid one, provided the paid-up value meets the threshold. We check whether your specific property and loan structure qualifies.',
  },
  {
    Icon: GraduationCap,
    title: 'Top students don’t always need a co-sponsor',
    desc: 'Outstanding students and recent graduates from eligible UAE and top-ranked global universities can, in many cases, obtain their own Golden Visa independent of a parent’s sponsorship — a route many families overlook.',
  },
  {
    Icon: Briefcase,
    title: 'Specialised talent isn’t only for the ultra-famous',
    desc: 'The specialised-talent category covers a working list of professions — doctors, engineers, scientists, executives, creatives and more — not just celebrities or Nobel laureates. Many qualified professionals underestimate their own eligibility.',
  },
  {
    Icon: Airplane,
    title: 'You can be outside the UAE for longer without losing it',
    desc: 'Golden Visa holders generally have more flexibility on how long they can stay outside the UAE without the residency lapsing, compared to standard visas — useful for investors and professionals who travel frequently.',
  },
];

const GOLDEN_SERVICES = [
  { label: 'Family Visa',           to: '/visas/family-visa',    desc: 'Sponsor spouse & children' },
  { label: 'Corporate Travel',      to: '/corporate-travel',     desc: 'For investors & executives' },
  { label: 'Flights',               to: '/flights',              desc: 'Business & first class fares' },
  { label: 'Hotel Booking',         to: '/hotels',                desc: 'Abu Dhabi & Dubai stays' },
  { label: 'All Visa Services',     to: '/visas',                 desc: 'UAE, Schengen, UK, USA & more' },
  { label: 'Contact Our Team',      to: '/contact',               desc: 'Mussafah, Abu Dhabi office' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'UAE Golden Visa Application Assistance',
  description: 'UAE Golden Visa assistance — 5 and 10-year renewable residency for investors, entrepreneurs, specialised talents, skilled employees and outstanding students, in Abu Dhabi and Dubai. Based in Mussafah, Abu Dhabi.',
  url: 'https://bigbirdtravel.com/visas/golden-visa',
  provider: {
    '@type': 'TravelAgency',
    name: 'Big Bird Travel & Tourism',
    url: 'https://bigbirdtravel.com',
    telephone: '+971561789642',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
      addressLocality: 'Mussafah',
      addressRegion: 'Abu Dhabi',
      addressCountry: 'AE',
    },
    email: 'info@bigbirdtravel.com',
    openingHours: 'Mo-Sa 09:00-18:00',
  },
  areaServed: { '@type': 'Country', name: 'United Arab Emirates' },
  serviceType: 'UAE Golden Visa Assistance',
  termsOfService: 'https://bigbirdtravel.com/terms',
};

function GoldenVisaExtraSections() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />

      {/* 1. At-a-Glance */}
      <section className="py-10 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-4">UAE Golden Visa — At a Glance</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SUMMARY_CARDS.map(({ label, value, sub, Icon }) => (
              <div key={label} className="rounded-2xl border border-[#C3DCF5] bg-white p-4 shadow-sm flex flex-col gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={16} weight="fill" className="text-[#0068B3]" />
                </span>
                <p className="text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{label}</p>
                <p className="font-display font-black text-[#071126] text-[14px] leading-tight">{value}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Who this guide is for */}
      <section className="py-10 px-6 lg:px-8 bg-white border-b border-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          <div className="shrink-0 lg:w-60">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">About This Guide</span>
            <h2 className="font-display font-black text-[#071126] text-[20px] leading-tight">Who this is for</h2>
            <p className="text-gray-500 text-[13px] mt-2 leading-relaxed">
              For investors, entrepreneurs, professionals and students in Abu Dhabi and Dubai considering long-term UAE residency.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUIDE_FOR.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-[#D8EAF7] bg-[#F5FAFF] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={14} weight="fill" className="text-[#0068B3]" />
                </span>
                <span className="text-[13px] font-semibold text-[#071126]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. AI Answer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
        <AIAnswerBox
          question="What is the UAE Golden Visa and who qualifies in 2026?"
          answer="The UAE Golden Visa is a long-term, renewable residence visa (5 or 10 years) that does not require a local sponsor or employer. It is open to real estate investors, business investors/entrepreneurs, specialised talents (doctors, scientists, executives, creatives), highly-skilled employees, outstanding students/graduates, and recognised humanitarian pioneers. Each category has its own investment, salary or qualification threshold. Big Bird Travel, based in Mussafah, Abu Dhabi, assesses which category fits an applicant and manages the application."
        />
        <AIAnswerBox
          question="Can Golden Visa holders sponsor their family without a minimum salary?"
          answer="Yes, in most cases. Golden Visa holders can typically sponsor their spouse and children — and in many cases parents — without the minimum salary requirement that applies to standard UAE employment-visa sponsorship. This is one of the visa's key advantages over a standard employer-sponsored residence visa."
        />
      </div>

      {/* 4. Category table */}
      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Eligibility Categories</span>
          <h2 className="font-display font-black text-[#071126] mb-2" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.65rem)' }}>
            UAE Golden Visa categories — 2026
          </h2>
          <p className="text-gray-500 text-[13px] mb-6 max-w-2xl">
            Thresholds and requirements are set by UAE federal and emirate authorities and are periodically updated — confirm your exact eligibility with us before applying.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#D8EAF7] shadow-sm">
            <table className="w-full text-left text-[13px] min-w-[650px]">
              <thead>
                <tr className="bg-[#071126] text-white">
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tl-2xl">Category</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Duration</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Key Requirement</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tr-2xl text-center">Check</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORY_TABLE.map((row, i) => (
                  <tr key={row.category} className={`border-t border-[#E8F3FC] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5FAFF]'}`}>
                    <td className="px-5 py-3.5 font-semibold text-[#071126]">{row.category}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                        row.badge === 'green' ? 'bg-green-100 text-green-800' :
                        row.badge === 'blue'  ? 'bg-[#EAF7FF] text-[#0068B3]' :
                        row.badge === 'navy'  ? 'bg-[#071126] text-white' :
                                                'bg-gray-100 text-gray-600'
                      }`}>{row.duration}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{row.key}</td>
                    <td className="px-5 py-3.5 text-center">
                      <a
                        href={buildWAUrl(`Hi Big Bird Travel, I want to check my eligibility for the UAE Golden Visa under the "${row.category}" category. Please advise.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0F7A35] text-white text-[11px] font-black px-3 py-1.5 hover:bg-[#0a6029] transition-colors whitespace-nowrap"
                      >
                        <WhatsappLogo size={12} weight="fill" /> Check
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 5. Know before you apply */}
      <section className="py-14 px-6 lg:px-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 mb-3 block">⚠ Know Before You Apply</span>
            <h2 className="font-display font-black text-[#071126] mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Details that trip up Golden Visa applicants
            </h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              The most common mistake is applying under the wrong category — for example, a skilled professional applying as an investor, or vice versa. The second is incomplete document attestation, especially for degrees and family certificates.
            </p>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-6">
              We assess your profile against the current criteria first, before you spend time or money on the wrong category.
            </p>
            <div className="space-y-2.5">
              <ExpandableTip title="Match your category before you apply">
                Investor, professional, student and humanitarian categories each have different documents, thresholds and processing routes. Applying under the wrong one wastes time. We identify the right category from your profile first.
              </ExpandableTip>
              <ExpandableTip title="Attest family certificates early">
                Marriage and birth certificates for family sponsorship need attestation by the UAE Ministry of Foreign Affairs (and often the issuing country first). This step alone can take longer than the visa application — start it early.
              </ExpandableTip>
              <ExpandableTip title="Property investors: check the paid-up value">
                If your property is mortgaged, only the paid-up equity may count toward the investment threshold, depending on the bank and case. We verify this against the current rules before you apply.
              </ExpandableTip>
              <ExpandableTip title="Renewal depends on still meeting the criteria">
                A Golden Visa renews as long as you still meet your category's requirements (e.g., still holding the qualifying investment or role) — not automatically. We flag renewal requirements well ahead of expiry.
              </ExpandableTip>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} weight="fill" className="text-[#0068B3]" />
              <span className="font-black text-[12px] uppercase tracking-widest text-[#0068B3]">Quick Eligibility Check</span>
            </div>
            <ul className="space-y-3 mb-5">
              {[
                { text: 'Own property above the threshold', result: 'Investor category', type: 'ok' },
                { text: 'Run or invest in a UAE company', result: 'Business investor category', type: 'ok' },
                { text: 'Doctor, engineer, exec, scientist', result: 'Specialised talent category', type: 'ok' },
                { text: 'Top grades, eligible university', result: 'Student category', type: 'ok' },
                { text: 'Not sure which category fits', result: 'Message us your profile', type: 'warn' },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-2.5">
                  {item.type === 'ok' && <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0 mt-0.5" />}
                  {item.type === 'warn' && <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
                  <span className="text-[12px] text-gray-700"><strong>{item.text}</strong> → {item.result}</span>
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0F7A35] text-white text-[13px] font-black py-3.5 hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Check My Eligibility — Free
            </a>
            <p className="text-center text-[11px] text-gray-400 mt-2.5 leading-snug">
              Send your profile — honest answer, same day.
            </p>
          </div>
        </div>
      </section>

      {/* 6. What sites don't tell you */}
      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#071126] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#C9A227] mb-3 block">Authority Section</span>
          <h2 className="font-display font-black text-white mb-3" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
            What most Golden Visa pages don't tell you
          </h2>
          <p className="text-blue-50/60 text-[14px] mb-8 max-w-2xl">
            The headline benefits are everywhere. These are the details that decide whether your application — or your renewal — goes smoothly.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIDDEN_FACTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#0096FF]/20 bg-white/[0.045] p-5 hover:border-[#0096FF]/40 hover:bg-white/[0.06] transition-all">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0096FF]/10 border border-[#0096FF]/20 mb-4">
                  <Icon size={17} weight="fill" className="text-[#0096FF]" />
                </span>
                <h3 className="font-display font-black text-white text-[13px] mb-2 leading-tight">{title}</h3>
                <p className="text-blue-50/60 text-[12px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-[#0096FF]/25 bg-white/[0.04] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-display font-black text-white text-[15px] mb-1">Not sure which Golden Visa category fits you?</p>
              <p className="text-blue-50/60 text-[13px]">Send your profile — investment, role, salary or grades. We'll confirm the right category and an honest cost — usually within the hour.</p>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-5 py-3 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* 7. How Big Bird helps */}
      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#041024] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.14),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0096FF] mb-3 block">Why Big Bird</span>
            <h2 className="font-display font-black text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Golden Visa, handled by one UAE team
            </h2>
            <p className="text-blue-50/85 text-[15px] italic leading-relaxed mb-5 border-l-2 border-[#0096FF] pl-4">
              "Send your profile — investment, role, salary or grades. We'll confirm your eligibility category, prepare your documents, and track the application end to end."
            </p>
            <p className="text-blue-50/70 text-[14px] leading-relaxed mb-4">
              We're a licensed travel agency in <strong className="text-white">Mussafah, Abu Dhabi</strong>. For Golden Visa applications we:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Match your profile to the correct eligibility category — no guesswork.',
                'Guide document attestation for degrees, family and business certificates.',
                'Prepare and submit the application through the correct UAE channel.',
                'Advise on family sponsorship once your Golden Visa is approved.',
                'Track renewal requirements well ahead of your expiry date.',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-blue-50/80">
                  <CheckCircle size={14} weight="fill" className="text-[#0096FF] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-6 py-3.5 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={19} weight="fill" /> WhatsApp Us — Same-Day Response
            </a>
          </div>
          <div className="space-y-4">
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-3xl p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/70 mb-4">Big Bird Travel — Agency Details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Office',   value: 'Mussafah 13, Abu Dhabi' },
                  { label: 'WhatsApp', value: '+971 56 178 9642' },
                  { label: 'License',  value: 'ADRA CN-5285999' },
                  { label: 'Response', value: 'Same day' },
                  { label: 'Hours',    value: 'Mon–Sat 9am–6pm UAE' },
                  { label: 'Serves',   value: 'Abu Dhabi · Dubai · UAE' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/60 mb-0.5">{item.label}</p>
                    <p className="text-[13px] font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-2xl p-5">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-[#C9A227]" />))}
                <span className="text-[11px] text-blue-50/50 ml-2">Verified customer</span>
              </div>
              <blockquote className="text-[13px] text-blue-50/80 italic leading-relaxed">
                "I wasn't sure if my business qualified for the investor category. They checked it properly, prepared everything, and my Golden Visa was approved without back-and-forth."
              </blockquote>
              <p className="text-[11px] text-blue-50/40 mt-2">— Business owner, Abu Dhabi</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Internal links */}
      <section className="py-12 px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Plan Your Move to the UAE</span>
          <h2 className="font-display font-black text-[#071126] mb-6 text-[20px]">
            Golden Visa sorted. Now plan family, travel and your base.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {GOLDEN_SERVICES.map(({ label, to, desc }) => (
              <Link key={label} to={to} className="group rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4 hover:border-[#0096FF]/50 hover:bg-[#EAF7FF] hover:-translate-y-0.5 transition-all flex flex-col gap-2">
                <ArrowRight size={15} weight="bold" className="text-[#0068B3] group-hover:translate-x-0.5 transition-transform" />
                <p className="font-display font-black text-[#071126] text-[13px] leading-tight">{label}</p>
                <p className="text-[11px] text-gray-400">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 9. E-E-A-T trust strip */}
      <section className="py-7 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><CalendarBlank size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Last Updated:</strong> September 2026</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><SealCheck size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Reviewed by:</strong> Big Bird Travel Visa Team</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><Shield size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">License:</strong> ADRA CN-5285999</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Office:</strong> Mussafah 13, Abu Dhabi, UAE</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><WhatsappLogo size={13} weight="fill" className="text-green-600" /><span><strong className="text-[#071126]">WhatsApp:</strong> Same-day response</span></div>
        </div>
      </section>

      {/* 10. Areas we serve */}
      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="shrink-0">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">Local Service</span>
              <h2 className="font-display font-black text-[#071126] text-[18px]">Areas We Serve</h2>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-[14px] mb-4">
                We assist Golden Visa applicants across <strong className="text-[#071126]">Abu Dhabi, Dubai, and the Northern Emirates</strong> — from our Mussafah office, with real one-on-one guidance.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Mussafah', 'Mohammed Bin Zayed City', 'Khalifa City', 'Al Nahyan', 'Abu Dhabi City', 'Al Ain', 'Dubai', 'Sharjah', 'Northern Emirates'].map(area => (
                  <span key={area} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF7FF] border border-[#C3DCF5] text-[12px] font-black text-[#0068B3]">
                    <MapPin size={11} weight="fill" />{area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function GoldenVisa() {
  return (
    <VisaDetailLayout
      visa={VISA_BY_ID['golden-visa']}
      extraSections={<GoldenVisaExtraSections />}
    />
  );
}
